import cn from 'classnames';
import React from 'react';

import { ButtonV3, InputV2 } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import { Modal } from 'components/Modal/Modal.component';
import { TextArea } from 'components/form';
import { Col, Row } from 'components/layout';
import { REGEX } from 'lib/utils/regex';

import styles from './AddNewCard.module.scss';

interface AddNewCardProps {
  isUrl: boolean;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave: (data: any) => void;
  placeHolder?: string;
  modalPlaceholders?: {
    title: string;
    subTitle: string;
  };
  itemCount?: number;
  isArticle?: boolean;
}

export const AddNewCard: React.FC<AddNewCardProps> = ({
  isUrl = false,
  title: modalTitle,
  onSave,
  placeHolder,
  modalPlaceholders,
  itemCount = 0,
  isArticle,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [title, setTitle] = React.useState<string>('');
  const [subTitle, setSubTitle] = React.useState<string>('');
  const [url, setUrl] = React.useState<string>('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const onClose = () => {
    setIsOpen(false);
    setTitle('');
    setSubTitle('');
    setUrl('');
    setErrors({});
  };

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    setErrors({ ...errors, title: '' });
  };

  const onSubTitleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSubTitle(event.target.value);
  };

  const onUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setErrors({ ...errors, url: '' });
  };

  const handleSaveClick = () => {
    let isInvalid = false;

    if (!title || (isUrl && (!url || !url.match(REGEX.URL)))) {
      setErrors({
        title: !title ? 'Title is required' : '',
        url:
          isUrl && (!url || !url.match(REGEX.URL)) ? 'Enter a valid Url' : '',
      });
      isInvalid = true;
    }

    if (isInvalid) {
      return;
    }

    const data = { title, ...(isUrl ? { url } : { value: subTitle }) };
    onSave(data);
    setIsOpen(false);
    setTitle('');
    setSubTitle('');
    setUrl('');
  };

  return (
    <>
      {isOpen ? (
        <Modal
          contentClassName={styles.AddNewCardModal}
          isOpen={isOpen}
          onRequestClose={onClose}
        >
          <div>
            <IconButton
              size="small"
              iconName="close"
              className={styles.cancelIcon}
              onClick={onClose}
            />
            <div className={styles.header}>{modalTitle}</div>

            <div className={styles.content}>
              <Row>
                <Col span={24} className={styles.contentFieldColumn}>
                  <InputV2
                    label="Title"
                    value={title}
                    onChange={onTitleChange}
                    labelClassName={styles.labelField}
                    placeholder={modalPlaceholders?.title}
                    isInvalid={!!errors.title}
                    helperText={errors.title}
                    maxLength={isArticle ? 160 : 120}
                  />
                </Col>
                <Col span={24} className={styles.contentFieldColumn}>
                  {isUrl ? (
                    <>
                      <InputV2
                        label="URL"
                        value={url}
                        onChange={onUrlChange}
                        labelClassName={styles.labelField}
                        placeholder={modalPlaceholders?.subTitle}
                        isInvalid={!!errors.url}
                        helperText={errors.url}
                      />
                    </>
                  ) : (
                    <>
                      <TextArea
                        rows={4}
                        label="Subtitle"
                        onChange={onSubTitleChange}
                        value={subTitle}
                        labelClassName={styles.labelField}
                        textAreaClassName={styles.textArea}
                        isValid
                        showOptionalLabel
                        placeholder={modalPlaceholders?.subTitle}
                        maxLength={400}
                      />
                    </>
                  )}
                </Col>
              </Row>
            </div>
            <div className={styles.footer}>
              <ButtonV3
                type="button"
                className={styles.footerSaveButton}
                onClick={handleSaveClick}
              >
                {modalTitle}
              </ButtonV3>
            </div>
          </div>
        </Modal>
      ) : (
        <>
          {placeHolder && (
            <div
              className={styles.card}
              onClick={() => {
                setIsOpen(true);
              }}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>{placeHolder}</div>
              </div>
            </div>
          )}
          <ButtonV3
            onClick={() => {
              setIsOpen(true);
            }}
            className={cn(styles.addButton, {
              [styles.addButtonSmall]: itemCount,
            })}
            startIcon="plus"
            variant="contained"
            color="secondary"
            type="button"
          >
            {modalTitle}
          </ButtonV3>
        </>
      )}
    </>
  );
};

AddNewCard.displayName = 'AddNewCard';
